import { useEffect, useMemo, useRef, useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import type { LiftViewModel } from '../../api/adapters';
import { StatusChip } from '../../design-system/components/StatusChip';
import { ConfirmDialog } from '../../design-system/components/ConfirmDialog';
import { RoleGuard } from '../../auth/RoleGuard';
import { audienceFor } from '../../auth/roles';
import { useAuth } from '../../auth/useAuth';
import {
  useAcquireLiftSession,
  useLiftAdvance,
  useLiftCall,
  useLiftClose,
  useLiftEndSession,
  useLiftRecover,
  useLiftWriteGate,
  useRenewLiftSession,
} from '../../api/hooks/useLifts';
import { LiftRobotUseSection } from './LiftRobotUseSection';
import { LiftWriteGatePanel } from './LiftWriteGatePanel';
import { liftDispatchStatus } from './lift-dispatch-status';
import { useActiveSite, useSafeSiteId } from '../../state/useActiveSite';
import { resolveSiteTimeZone } from '../Tasks/site-timezone';
import { releaseLiftSession } from '../../api/v1-client';
import type { LiftOperatorVerb, LiftSession, LiftVerbResponse, SiteId } from '../../api/v1';
import type { StatusKind } from '../../design-system/tokens';
import { useDismissOnOutside } from '../../shell/use-dismiss-on-outside';
import { mintPressNonce, operatorIdempotencyKey, useOperatorRequest } from './useOperatorRequest';
import { LiftReadingLines } from './LiftReadingLines';
import { formatClock, type LiftRobotFacts } from './lift-reading';
import { useLiftCarViews, useLiftColumnState } from './useLiftColumnState';
import { ShowWhereLink } from '../../shell/ShowWhereLink';
import './DeviceDetailDrawers.css';

/** No building facts: the drawer then shows no reading lines. */
const NO_CARS: readonly LiftRobotFacts[] = [];

/** A row value the car's own reading no longer vouches for: the row
 *  still carries it, but the car is not live (its door or motion) or
 *  not placed (its floor), so showing it would say two things at once
 *  under the reading lines. */
const NOT_CURRENT = 'not current';

/** Renew the nexsoss-owned panel session this far before it would
 *  otherwise expire, so an actively-viewed panel never lapses. */
const SESSION_RENEW_BEFORE_EXPIRY_MS = 15_000;
/** Auto-close the panel after this long with no click anywhere in
 *  it (button or pane area) -- an idle, forgotten-open panel should
 *  not indefinitely hold a lift session. Suspended while a verb is in
 *  flight (S7): closing the drawer mid-request would drop the verdict
 *  on the floor, which is the silence this release exists to end. */
const INACTIVITY_AUTO_CLOSE_MS = 60_000;

/** Upstream's `current_mode`. Same table as the Lifts tab's Mode
 *  column; kept in both because they are the same idea rendered twice,
 *  not two ideas. */
const LIFT_MODE_LABEL: Record<string, string> = {
  agv: 'AGV — a robot may hold the car',
  human: 'Human — passenger control',
  off: 'Offline — the car is not answering',
  fire: 'Fire — the fire panel owns the car',
  unknown: 'Unknown',
};

const MOTION_MAP: Record<string, StatusKind> = {
  idle: 'idle',
  moving: 'active',
  fault: 'error',
};
const DOOR_MAP: Record<string, StatusKind> = {
  open: 'active',
  closed: 'idle',
  moving: 'moving',
  fault: 'error',
};

/** The five operator verbs as the banner names them. "End lift
 *  session", not "End RMF session", for every reader (batch B, Akash
 *  2026-10-31): the verb's key keeps its wire name. */
const VERB_LABEL: Record<string, string> = {
  advance: 'Release lift',
  recover: 'Recover',
  end_rmf_session: 'End lift session',
  manual_call: 'Request Floor',
  manual_close: 'Cancel call',
};

const STATUS_LABEL: Record<string, string> = {
  done: 'Done',
  refused: 'Refused',
  failed: 'Failed',
  timeout: 'Timed out',
};

/**
 * The robot-held car's controls in the hotel's words (batch B, Akash
 * 2026-10-31: "Rename and keep"). End lift session and Recover stay for
 * the hotel roles that hold them: the hotel does its own recovery (D26),
 * so nothing here says to wait for Nexsoss. Ops keep the precise text
 * (the fleet adapter's claim, the bridge, AGV mode). Both say the same
 * thing about the building: nothing moves, and while the robot is in the
 * car the car is called again.
 */
const SITE_LIFT_WORDS = {
  // What happens to the car is the point (review BC-F3, 2026-10-31):
  // once the robot is out of the cabin the car goes straight back to
  // guests, doorway or not (the comment on `pendingEndSession` below:
  // "it can strand a robot in a doorway"). So the robot comes out of the
  // car AND clear of the doors before the press, as the manual's C3.2
  // checks say, and "press again" is only for a press made while it was
  // still inside.
  recoverTitle:
    "Ends the robot's hold on this lift and cancels its job. It moves nothing: get the robot out of the car and clear of the doors first.",
  endSessionTitle:
    "Ends the robot's hold on this lift. Its job is kept, so it may call the lift again. It moves nothing: get the robot out of the car and clear of the doors first.",
  stillInCar:
    'This moves nothing. Get the robot out of the car and clear of the doors before you ' +
    'press: once it is out of the car, the car goes back to guests at once, even with the ' +
    'robot still in the doorway. While it is still in the car, the system keeps the car and ' +
    'calls it again; if you pressed then, press again once it is out.',
  agvWait: (remainingS: number | null) =>
    `Waiting for the lift to let the robot in${remainingS !== null ? ` (${remainingS}s)` : ''}. ` +
    'Nobody can confirm this wait by hand.',
  notConfirmable: 'The ride is waiting on a step nobody can confirm by hand.',
  nothingWaiting: 'The ride is not waiting on anything you can confirm.',
} as const;

/**
 * The one step a site reader is asked to confirm, by the wait's name
 * (review BC-F2, 2026-10-31). The confirm button showed the wire name
 * ("Confirm car_arrival"), and the dialog listed all three steps a wait
 * can be, so a reader who saw the car standing open would confirm a
 * `clearance` wait -- telling the bridge the robot was clear of the doors
 * -- and the car was released with it in the doorway. Each name now
 * reads as the one thing to look for, in the button and the dialog
 * alike. Names: the IO mainboard's (`car_arrival`, `door_open`,
 * `clearance`, adapters-lift `sim/board.py`) and the edge sequence's
 * (`car_call_ack`, `call_processed`, `trip.ack_board`, `trip.ack_exit`,
 * `orchestrator/sequence.py`). A name not listed gets no guess: "this
 * step", and the dialog says only that it must be seen. Ops keep the
 * wire name.
 */
const SITE_WAIT_WORDS: ReadonlyArray<{ match: (name: string) => boolean; sees: string }> = [
  { match: (n) => n === 'car_arrival', sees: 'the car has arrived' },
  { match: (n) => n === 'door_open', sees: 'the doors are open' },
  { match: (n) => n === 'clearance', sees: 'the robot is fully clear of the doors' },
  { match: (n) => n === 'car_call_ack', sees: 'the lift has taken the call' },
  { match: (n) => n === 'call_processed', sees: 'the car is at the floor with its doors open' },
  { match: (n) => n.startsWith('trip.ack_board'), sees: 'the robot is inside the car' },
  { match: (n) => n.startsWith('trip.ack_exit'), sees: 'the robot is out of the car' },
];

/** What a site reader confirms for this wait; null when the name is not
 *  one the console knows. */
function siteWaitSees(name: string | null | undefined): string | null {
  if (!name) return null;
  return SITE_WAIT_WORDS.find((w) => w.match(name))?.sees ?? null;
}

/** The site reader's confirm button: the one step, in words. */
function siteConfirmLabel(name: string, remainingS: number | null): string {
  const sees = siteWaitSees(name);
  return `${sees ? `Confirm ${sees}` : 'Confirm this step'}${remainingS !== null ? ` (${remainingS}s)` : ''}`;
}

/** What follows a confirm, for a site reader (final gate, 2026-10-31).
 *  It said "It moves nothing", which is true of the press and false of
 *  what it lets happen: a confirmed `door_open` sends the robot in, a
 *  confirmed `clearance` releases the car (the session goes idle,
 *  adapters-lift e2e `abort_and_clearance`). Recover and End lift
 *  session keep "moves nothing": there nothing moves the robot. */
const SITE_CONFIRM_THEN =
  'As soon as you confirm, the ride carries on: the robot or the car may start to move.';

/** The site reader's confirm dialog, naming the one step. */
function siteConfirmMessage(liftName: string, name: string | null | undefined): string {
  const sees = siteWaitSees(name);
  return sees
    ? `${liftName} is waiting for you to confirm ${sees}. Only do this if you can SEE that ` +
        `${sees}. ${SITE_CONFIRM_THEN}`
    : `${liftName} is waiting for a step the system cannot see. Only confirm it if you can SEE ` +
        `that it has happened. ${SITE_CONFIRM_THEN}`;
}

/**
 * What a verdict code MEANS to the person reading it. The adapter's and
 * api-server's `reason` strings are written for logs; these are written
 * for the operator, and fall back to the wire reason for a code not
 * listed here.
 */
const VERDICT_CODE_TEXT: Record<string, string> = {
  NOT_ADVANCEABLE:
    'This wait cannot be confirmed by an operator (the car itself has to grant it). Nothing was changed.',
  NOTHING_WAITING: 'The ride is not waiting on anything right now.',
  WRONG_PHASE: 'The ride moved on before the confirmation arrived. Look again before pressing.',
  WRONG_OWNER: 'This verb applies to a robot-held car, and this car is not held by a robot.',
  HELD_BY_ROBOT: 'A robot holds this car. Wait for its ride to finish, or end its session first.',
  IN_FLIGHT: 'A request is already being worked on this lift. Wait for its verdict.',
  NO_SESSION: 'There is no operator call to cancel on this lift.',
  WRITES_NOT_ARMED: 'Lift writes are not armed at the edge. Arm the site from the write-gate panel.',
  INVENTORY_NOT_VALIDATED: 'The lift inventory has not been validated at the edge; every write is refused.',
  AGV_NOT_GRANTED: 'The car did not grant AGV control in time. The call was withdrawn.',
  ADAPTER_DEADLINE: 'The ride did not complete within the adapter’s deadline.',
  LIFT_MODE_NOT_NORMAL: 'The car is not in normal service, so it cannot be called.',
  NO_VERDICT: 'No verdict reached the console. The verdict path is not delivering.',
  // api-server 0.6.33. Lowercase because it is an `ApiErrorCode`, not an
  // adapter verdict code. This console mints a fresh key per press, so
  // it should only ever see this if something reused one deliberately.
  idempotency_key_stale:
    'This press reused an old request key, so nothing was sent. Reload the console and press again.',
};

/**
 * The same codes in the hotel's words (review BC-F9, 2026-10-31): the
 * table above says "verb", "AGV control", "at the edge", "inventory" and
 * "the adapter's deadline", and a site reader's banner showed the code
 * itself in brackets. A code not listed falls back to a sentence by the
 * verdict's status (`SITE_STATUS_TEXT`), never to the wire reason.
 */
const SITE_VERDICT_CODE_TEXT: Record<string, string> = {
  NOT_ADVANCEABLE: 'Nobody can confirm this step by hand: the lift itself has to do it. Nothing was changed.',
  NOTHING_WAITING: 'The ride is not waiting on anything right now.',
  WRONG_PHASE: 'The ride moved on before your confirmation arrived. Look again before pressing.',
  WRONG_OWNER: 'This only works while a robot holds the car, and no robot holds it now.',
  HELD_BY_ROBOT: 'A robot holds this car. Wait for its ride to finish, or end its lift session first.',
  IN_FLIGHT: 'A press on this lift is still waiting for its answer. Wait for it.',
  NO_SESSION: 'You have no call on this lift to cancel.',
  WRITES_NOT_ARMED:
    'Lift control is switched off for this site, so nothing was sent. A supervisor can switch it on under Dispatching writes.',
  INVENTORY_NOT_VALIDATED: "The lifts haven't been checked and set up yet, so every press is refused. Call Nexsoss.",
  AGV_NOT_GRANTED: "The lift didn't hand over control in time, so the call was withdrawn.",
  ADAPTER_DEADLINE: "The ride didn't finish in time.",
  LIFT_MODE_NOT_NORMAL: 'The car is not in normal service, so it cannot be called.',
  NO_VERDICT: 'No answer came back. It does not mean nothing happened: look at the car.',
  idempotency_key_stale: 'This press repeated an old one, so nothing was sent. Reload the console and press again.',
};

/** A site reader's sentence for a verdict whose code has none above. */
const SITE_STATUS_TEXT: Record<string, string> = {
  done: 'Done.',
  refused: 'The lift system refused it. Look again before pressing.',
  failed: "It couldn't be sent. Press again; if it keeps failing, call Nexsoss.",
  timeout: 'No answer came back. It does not mean nothing happened: look at the car.',
};

/** A site reader's verdict sentence. "View as" keeps its own calm words
 *  (`verdictFromError`'s PREVIEW_MODE): they are the console's, not the
 *  lift's. */
function siteVerdictText(v: { code: string | null; status: string; reason: string }): string {
  if (v.code === 'PREVIEW_MODE') return v.reason;
  return (
    (v.code ? SITE_VERDICT_CODE_TEXT[v.code] : undefined) ??
    SITE_STATUS_TEXT[v.status] ??
    SITE_STATUS_TEXT.refused!
  );
}

/** The operator call's phases, in the order an operator sees them. A
 *  site reader's words are `site` (BC-F9: "requesting AGV control"). */
const CALL_PROGRESS: ReadonlyArray<{ phase: string; label: string; site: string }> = [
  { phase: 'requesting_agv', label: 'requesting AGV control', site: 'asking the lift for the car' },
  { phase: 'moving', label: 'moving', site: 'moving' },
  { phase: 'held', label: 'held', site: 'held for you' },
];

/** Who holds the car, for a site reader: a robot by its name, never its
 *  wire id (`phantas_fleet/Phantas-3`), and no phase (BC-F9). */
function siteOwnerText(owner: { kind: string; id: string } | null): string {
  if (!owner) return '— (none)';
  if (owner.kind === 'robot') {
    const name = owner.id.includes('/') ? owner.id.slice(owner.id.lastIndexOf('/') + 1) : owner.id;
    return name ? `A robot (${name})` : 'A robot';
  }
  if (owner.kind === 'operator') return 'Someone at the console';
  return 'Someone else';
}

/** The worker's stamp (`lastSeen`, ISO) as a time on the site's clock
 *  (`formatClock`); "—" when there is none or it cannot be read. */
function lastStampText(
  lastSeen: string | undefined,
  timeZone: string | null,
  wallNowMs: number,
): string {
  const ms = lastSeen ? Date.parse(lastSeen) : Number.NaN;
  return Number.isFinite(ms) ? formatClock(ms, timeZone, wallNowMs) : '—';
}

/** Whole seconds until an epoch-SECONDS stamp, floored at 0; null when
 *  there is no stamp. Only ever fed `*_epoch_ts` fields -- the edge's
 *  monotonic `deadline_ts` must never reach here. */
function secondsUntil(epochS: number | null | undefined, nowMs: number): number | null {
  if (typeof epochS !== 'number' || !Number.isFinite(epochS)) return null;
  return Math.max(0, Math.ceil(epochS - nowMs / 1000));
}

export function LiftDetailDrawer({
  lift,
  onClose,
  cars,
  focusRules = false,
}: {
  lift: LiftViewModel;
  onClose: () => void;
  /** Opened by the Lifts table's Change…: the Robot use section is brought
   *  into view. */
  focusRules?: boolean;
  /**
   * The site's cars with their building facts (W1's asset lift list,
   * `liftFactsFromAsset`), the same list every lift surface reads. With
   * it the drawer opens on the car's Guests and Robots lines; without it,
   * or for a car it does not name, there are no lines, never guessed ones.
   */
  cars?: readonly LiftRobotFacts[] | null;
}) {
  const panelRef = useRef<HTMLElement>(null);
  useDismissOnOutside(panelRef, onClose);

  const siteId = useSafeSiteId();
  const { roles } = useAuth();
  // Who reads the rows below. `audienceFor`, never a role list of our
  // own: a mixed account reads ops, and `service_operator` reads site
  // (amendment A2).
  const audience = audienceFor(roles);
  // The site's clock for the worker's stamp; null when the site declares
  // no zone, and `formatClock` then labels the time "(device time)".
  const { sites } = useActiveSite();
  const zone = resolveSiteTimeZone(sites, siteId);
  const siteZone = zone.source === 'site' ? zone.timeZone : null;

  const gateQuery = useLiftWriteGate();
  // The one reading of this car (W6): the same receipts, freshness and
  // words as the map, the building view and the attention list.
  const liftRows = useMemo(() => [lift], [lift]);
  const columns = useLiftColumnState({
    cars: cars ?? NO_CARS,
    lifts: liftRows,
    gate: gateQuery.data,
  });
  const column = columns.get(lift.name) ?? null;
  // The car's look needs no building facts, so the rows below read it
  // whether or not the drawer was given them: floor while the car is
  // placed, door and motion only while it is live (`liftCarView`).
  const liftNames = useMemo(() => [lift.name], [lift.name]);
  const carView = useLiftCarViews(liftNames, liftRows).get(lift.name) ?? null;
  const acquireSession = useAcquireLiftSession();
  const renewSession = useRenewLiftSession();
  // THE FIVE OPERATOR VERBS, each its own route and capability, each
  // answered by a per-request verdict the adapter writes to
  // `session.last_result` -- never by the CommandRecord, which freezes
  // at "accepted" the moment the worker publishes.
  //
  // Request Floor is `call` (manual_call), NOT `useLiftCommand`'s
  // `request_destination`: that verb went to rmf_lift_supervisor under
  // a `nexsoss-worker-*` id the supervisor re-presented at 1 Hz forever
  // and the adapter re-opened every ~36 s. Nothing in this drawer sends
  // `request_destination` any more; the api-server answers 410 for it.
  const advance = useLiftAdvance();
  const recover = useLiftRecover();
  const endSession = useLiftEndSession();
  const call = useLiftCall();
  const close = useLiftClose();
  const [session, setSession] = useState<LiftSession | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const sessionRef = useRef<LiftSession | null>(null);
  const renewingRef = useRef(false);
  const lastActivityRef = useRef(Date.now());

  // Server-owned session (plan §Command lifecycle): acquire on open,
  // release on close/unmount/lift-change. If acquisition resolves
  // after the drawer has already closed (`cancelled`), release
  // immediately instead of leaking a session no UI will ever release.
  useEffect(() => {
    let cancelled = false;
    setSession(null);
    setSessionError(null);
    sessionRef.current = null;
    acquireSession.mutate(lift.name, {
      onSuccess: (res) => {
        if (cancelled) {
          void releaseLiftSession(siteId as SiteId, { session_id: res.session.session_id });
          return;
        }
        sessionRef.current = res.session;
        setSession(res.session);
      },
      onError: (err) => {
        if (!cancelled) setSessionError(err instanceof Error ? err.message : 'Failed to acquire lift session');
      },
    });
    return () => {
      cancelled = true;
      if (sessionRef.current) {
        void releaseLiftSession(siteId as SiteId, { session_id: sessionRef.current.session_id });
        sessionRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- acquireSession/releaseSession are mutation objects, re-run only on lift/site change
  }, [lift.name, siteId]);

  const [destination, setDestination] = useState<string>(lift.destinationLevel ?? lift.currentLevel ?? '');
  // No door-mode select any more. The edge's session family has no
  // door-mode input, so the old third field was a UX misread waiting to
  // happen: "Open" chosen, doors closed. Floor only.
  const [now, setNow] = useState(() => Date.now());
  const [pendingDestination, setPendingDestination] = useState<string | null>(null);
  // Every consequential verb confirms. End lift session hands the car
  // back with the robot wherever it is -- it can strand a robot in a
  // doorway. Recover does the same and ALSO cancels the robot's task; it
  // does not move the car or the robot first (corrected 2026-09-15: the
  // edge's `session.recover` stops the sequence, drops any door hold and
  // releases AGV mode, and nothing else). Both now also ask the fleet
  // adapter to drop RMF's claim on the car (worker 0.4.27, `release_lift`),
  // which it refuses while the robot is in the cabin. Release supplies
  // an observation the bridge could not make itself.
  const [pendingEndSession, setPendingEndSession] = useState<boolean>(false);
  const [pendingRecover, setPendingRecover] = useState<boolean>(false);
  const [pendingAdvance, setPendingAdvance] = useState<boolean>(false);

  // The one reading of the adapter's session view (S5). Everything the
  // drawer OFFERS keys off these, so that it can only offer a verb the
  // adapter would accept: Release only on an advance-eligible wait,
  // Cancel only on an operator-owned session, Recover/End only on a
  // robot-owned one.
  const sessionView = lift.session;
  const sessionActive = sessionView?.active === true;
  const owner = sessionActive ? sessionView.owner : null;
  const operatorOwned = owner?.kind === 'operator';
  const robotOwned = owner?.kind === 'robot';
  const wait = sessionActive ? sessionView.wait : null;
  const advanceEligible = wait?.advance_eligible === true;
  // The phase the operator is looking at, echoed back on the advance so
  // the bridge can refuse a confirmation that no longer describes what
  // is happening. A stalled session can move the instant it un-stalls.
  const liftPhase = lift.sessionPhase ?? undefined;
  const standingRefusal = sessionView?.refusal ?? null;
  const legacyStanding =
    lift.standingRequest && lift.standingRequest.session_id.startsWith('nexsoss-worker-')
      ? lift.standingRequest
      : null;

  const op = useOperatorRequest(lift, now);
  const anyPending =
    advance.isPending || recover.isPending || endSession.isPending || call.isPending || close.isPending;
  // GAP 8: while a request is in flight every verb on this lift is
  // disabled EXCEPT Cancel call on an operator-owned session -- the call
  // being worked is the one thing the operator may need to abort, and
  // its verdict arrives only at ride arrival.
  const busy = anyPending || op.state.kind === 'inflight';
  const busyRef = useRef(busy);
  busyRef.current = busy;
  // Set the instant a verb is handed to its mutation and cleared when
  // the mutation settles. `busyRef` is only as fresh as the last RENDER:
  // between `mutate()` and the render that shows `isPending`, a second
  // handler running in the same tick (two ConfirmDialog keydown
  // listeners answering one Enter, or a confirm clicked before React
  // has committed) would read `busy === false` and send a second verb,
  // whose `op.begin` then overwrites the first press's request_id and
  // drops its verdict.
  const sendingRef = useRef(false);
  // One nonce per operator PRESS, per verb, folded into the idempotency
  // key (`operatorIdempotencyKey`). Minted when a confirm dialog OPENS,
  // so everything that fires for that dialog -- a double-tap, two
  // keydown listeners answering one Enter -- shares one key and the
  // api-server replays instead of publishing twice. Dropped when the
  // press's request settles, so the next press can never reuse it.
  // Cancel call has no dialog, so its nonce is minted lazily on the
  // first run and dropped on settle the same way.
  //
  // WHY: without it an End session / Recover key was identical for every
  // press on a robot whenever the projection had no `last_result`, and
  // the api-server answered the 2026-09-14 presses at sg-nomad-01 with
  // records from 2026-08-31 and 2026-09-04 -- 200, nothing published.
  const pressNoncesRef = useRef<Partial<Record<LiftOperatorVerb, string>>>({});
  const openPress = (verb: LiftOperatorVerb) => {
    pressNoncesRef.current[verb] = mintPressNonce();
  };
  const pressNonce = (verb: LiftOperatorVerb): string => {
    const existing = pressNoncesRef.current[verb];
    if (existing) return existing;
    const minted = mintPressNonce();
    pressNoncesRef.current[verb] = minted;
    return minted;
  };

  // S7, second half: the verdict's ARRIVAL is activity. `lastActivityRef`
  // is otherwise bumped only by clicks, so a verdict landing more than
  // INACTIVITY_AUTO_CLOSE_MS after the press (recover's deadline is 90 s
  // in api-server `_VERB_DEADLINE_S`; its NO_VERDICT fires at 95 s) would
  // be auto-closed off the screen on the very next 1 Hz tick: the operator
  // watches a spinner for 90 s and the answer vanishes with the drawer.
  // `op.state` changes identity only on a transition (useOperatorRequest
  // never re-sets an existing verdict), so this fires once per verdict and
  // the drawer still auto-closes 60 s later if nobody reads it.
  useEffect(() => {
    if (op.state.kind === 'verdict') lastActivityRef.current = Date.now();
  }, [op.state]);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const handle = window.setInterval(() => {
      const t = Date.now();
      setNow(t);

      // Auto-close after a stretch of no clicks anywhere in the
      // panel — an idle, forgotten-open drawer shouldn't indefinitely
      // hold a lift session. NOT while a request is in flight (S7):
      // the verdict it is waiting on has nowhere else to land.
      if (!busyRef.current && t - lastActivityRef.current > INACTIVITY_AUTO_CLOSE_MS) {
        onCloseRef.current();
        return;
      }

      // Auto-renew the nexsoss-owned panel session shortly before it
      // would otherwise expire, reusing the SAME session_id (a fresh
      // id per "refresh" would look like a competing holder to the
      // worker's lock — see `lift_sessions.py::renew`'s docstring).
      // As long as the user keeps the panel open, this should make
      // expiry a non-event in normal use.
      const current = sessionRef.current;
      if (
        current &&
        !renewingRef.current &&
        current.expires_at_ms - t < SESSION_RENEW_BEFORE_EXPIRY_MS &&
        current.expires_at_ms > t
      ) {
        renewingRef.current = true;
        renewSession.mutate(current.session_id, {
          onSuccess: (res) => {
            renewingRef.current = false;
            sessionRef.current = res.session;
            setSession(res.session);
          },
          onError: () => {
            renewingRef.current = false;
            // Leave `session` as-is; `sessionExpired` will correctly
            // flip once its own `expires_at_ms` passes.
          },
        });
      }
    }, 1000);
    return () => window.clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- renewSession is a mutation object; onClose/busy are read through refs
  }, []);

  const sessionExpired = session !== null && now > session.expires_at_ms;
  // The one place this drawer decides what the lift's state MEANS --
  // the same function the Lifts table's Dispatch column calls. It
  // answers `no_state` first for a registry-only car, so the verdict
  // block below already explains the row before the operator reaches
  // the form.
  const dispatch = liftDispatchStatus(lift, gateQuery.data);
  // A car the building map knows about that is publishing nothing. No
  // verb may be offered on it: every one of them is answered by a
  // verdict the adapter writes to `session.last_result`, and an adapter
  // that is not publishing state will not be publishing verdicts
  // either -- the press would hang until NO_VERDICT. The Request Floor
  // path is the one that needs saying explicitly, because the others
  // are already gated on a session this lift cannot have.
  const noLiveState = !lift.hasLiveState;
  const noLiveStateTitle =
    'Nothing is publishing state for this lift, so the console cannot call it or observe the result. Check the lift bridge on the edge box.';
  // Request Floor keeps the 60 s panel lease (decision D6): two
  // operators must not drive one car at once, and that is what the
  // lease is for.
  const leaseMissing = !session || sessionExpired;

  const markActivity = () => {
    lastActivityRef.current = Date.now();
  };

  /**
   * Every verb goes through here so every one of them enters the same
   * state machine: the response's `request_id` starts the wait, the
   * error is a verdict at once. `markActivity` too, so a confirm dialog
   * that sat open for a while does not count as inactivity.
   */
  function runVerb<B>(
    verb: LiftOperatorVerb,
    m: UseMutationResult<LiftVerbResponse, Error, { lift: string; body: B }>,
    body: B,
  ) {
    markActivity();
    // The LAST line of defence for "one verb in flight per lift". The
    // buttons are disabled while busy or while a dialog is open, but a
    // ConfirmDialog's onConfirm can run after the state it was opened
    // in has moved on (it has no focus trap; Tab reaches the drawer
    // behind the backdrop), and none of the four onConfirm handlers can
    // know that. manual_close is the one exception (gap 8): the adapter
    // admits session.close from any phase and Cancel call is the only
    // way to withdraw a call that is itself in flight.
    if (verb !== 'manual_close' && (busyRef.current || sendingRef.current)) return;
    sendingRef.current = true;
    m.mutate(
      { lift: lift.name, body },
      {
        onSuccess: (res) => {
          sendingRef.current = false;
          delete pressNoncesRef.current[verb];
          op.begin(verb, res);
        },
        onError: (err) => {
          sendingRef.current = false;
          delete pressNoncesRef.current[verb];
          op.fail(verb, err);
        },
      },
    );
  }

  // While a confirm dialog is open no OTHER verb may be started: the
  // dialog is not modal to the keyboard, so without this two dialogs
  // could be open at once and one Enter would confirm both (each open
  // ConfirmDialog owns a window keydown listener).
  const dialogOpen =
    pendingAdvance || pendingRecover || pendingEndSession || pendingDestination !== null;

  const requestFloorTitle = noLiveState
    ? noLiveStateTitle
    : operatorOwned
      ? 'A call is in progress on this lift — cancel it first'
      : robotOwned
        ? `The car is held by a robot session (${owner?.id ?? ''})`
        : undefined;

  const autoCloseIn = operatorOwned ? secondsUntil(sessionView?.auto_close_epoch_ts, now) : null;
  const waitRemaining = wait ? secondsUntil(wait.deadline_epoch_ts, now) : null;

  return (
    <aside
      ref={panelRef}
      className="nx-device-drawer"
      role="dialog"
      aria-label={`Lift ${lift.name} details`}
      onClick={markActivity}
      onKeyDown={markActivity}
    >
      <header className="nx-device-drawer__head">
        <h2>{lift.name}</h2>
        {/* A link to the Overview, pointed at this car; never a drawer. */}
        <ShowWhereLink focus={{ kind: 'lift', liftName: lift.name }} />
        <button type="button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="nx-device-drawer__body">
        {column && <LiftReadingLines column={column} audience={audience} />}
        {noLiveState && (
          <p className="nx-device-drawer__sub" data-testid="lift-no-live-state">
            This lift is in the site’s registry — the building map knows it exists — but nothing is
            publishing state for it. Every reading below is an absence, not a measurement, and no
            command can be sent from here.
          </p>
        )}
        {/* Read through the car's look, as the map draws it: a car that
            is not placed (lost, or off with no fresh telemetry) has no
            floor and no destination here either. */}
        <div className="nx-device-drawer__row">
          <span>Current Floor</span>
          <span data-testid="lift-current-floor">
            {carView?.level ?? (lift.currentLevel && !noLiveState ? NOT_CURRENT : '—')}
          </span>
        </div>
        <div className="nx-device-drawer__row">
          <span>Destination</span>
          <span data-testid="lift-destination">
            {!lift.destinationLevel ? '—' : carView?.level ? lift.destinationLevel : NOT_CURRENT}
          </span>
        </div>
        {/* "no data" and "unknown" are different sentences, and this
            drawer has to say the same one its own table says about the
            same car. A registry-only row carries 'unknown' in these
            fields because the union has nowhere else to put "nobody
            said", and rendering that verbatim here read as MODE_UNKNOWN
            -- a car that answered and did not know -- while the Lifts
            tab was already showing the honest dash for it. */}
        <div className="nx-device-drawer__row">
          <span>Motion</span>
          {noLiveState ? (
            <StatusChip status="offline" label="no data" />
          ) : carView?.motion ? (
            <StatusChip
              status={MOTION_MAP[carView.motion] ?? 'offline'}
              pulse={carView.motion === 'moving'}
              label={carView.motion}
            />
          ) : (
            <StatusChip status="offline" label={NOT_CURRENT} />
          )}
        </div>
        <div className="nx-device-drawer__row">
          <span>Door</span>
          {noLiveState ? (
            <StatusChip status="offline" label="no data" />
          ) : carView?.door ? (
            <StatusChip status={DOOR_MAP[carView.door] ?? 'offline'} label={carView.door} />
          ) : (
            <StatusChip status="offline" label={NOT_CURRENT} />
          )}
        </div>
        <div className="nx-device-drawer__row">
          <span>Mode</span>
          <span data-testid="lift-mode">
            {noLiveState ? '—' : (LIFT_MODE_LABEL[lift.mode ?? 'unknown'] ?? lift.mode)}
          </span>
        </div>
        <div className="nx-device-drawer__row">
          <span>Levels</span>
          <span>{lift.levels.length > 0 ? lift.levels.join(', ') : '—'}</span>
        </div>
        {/* No Dimensions row: no metres in these views (doctrine), and
            a registry-only row's `depth` is the door's height. The
            Position is the map's frame, which a site audience never
            sees (plan §2 doctrine): ops only, and as the robot drawer
            shows a robot's, in a collapsed Details labelled as map
            units -- bare numbers read as metres. */}
        {audience === 'ops' &&
          lift.ref &&
          Number.isFinite(lift.ref.x) &&
          Number.isFinite(lift.ref.y) && (
            <details className="nx-device-drawer__details" data-testid="lift-details">
              <summary>Details</summary>
              <div className="nx-device-drawer__row">
                <span>Position</span>
                <span data-testid="lift-position">
                  {`x ${lift.ref.x.toFixed(1)}, y ${lift.ref.y.toFixed(1)} (map units, not metres)`}
                </span>
              </div>
            </details>
          )}
        {/* The worker's own stamp, shown as the time it is. Never an age:
            "1s ago" was this device's clock minus the worker's, and the
            worker re-stamps the row on every LiftState, so it looked
            fresh whatever the car was doing. Freshness is the lines'.
            On the SITE's clock, as every other new time is, with the day
            named when it is not today; "(device time)" when the site
            declares no zone (`formatClock`). `now` is only read to tell
            which date it is there, never subtracted from the stamp. */}
        <div className="nx-device-drawer__row">
          <span>Last stamp</span>
          <span data-testid="lift-last-stamp">{lastStampText(lift.lastSeen, siteZone, now)}</span>
        </div>
        {/* The supervisor's wire session id: an RMF id, for ops only. */}
        {audience === 'ops' && (
          <div className="nx-device-drawer__row">
            <span>RMF Session</span>
            <span data-testid="lift-rmf-session-id">{lift.rmfSessionId ?? '— (unclaimed)'}</span>
          </div>
        )}
        <div className="nx-device-drawer__row">
          <span>{audience === 'ops' ? 'Session Owner' : 'Held by'}</span>
          {/* The ADAPTER's belief, not the supervisor's echo: an
              operator-held car is never echoed as an RMF session id
              (decision D3), so this row is the only place it shows.
              A site reader gets the robot's name and no phase: the End
              lift session button already hides the wire id from them,
              and this row showed it right above it (BC-F9). */}
          <span data-testid="lift-session-owner">
            {audience === 'ops' ? (
              <>
                {owner ? `${owner.kind}: ${owner.id}` : '— (none)'}
                {sessionActive && liftPhase ? ` · ${liftPhase}` : ''}
              </>
            ) : (
              siteOwnerText(owner)
            )}
          </span>
        </div>
        {/* This panel's own lease on the car's controls: ops only. A
            site reader meets the one thing it means to them, an expired
            or unavailable lease, as the error line under the buttons. */}
        {audience === 'ops' && (
          <div className="nx-device-drawer__row">
            <span>Panel Session</span>
            <span data-testid="lift-session-state">
              {sessionError
                ? 'Unavailable'
                : sessionExpired
                  ? 'Expired'
                  : session
                    ? 'Active'
                    : 'Acquiring…'}
            </span>
          </div>
        )}

        {/* Placed ABOVE the command form on purpose: when the form's
            requests silently do nothing, this is the reason, and an
            operator should meet it before they meet the button. */}
        <div className="nx-device-drawer__verdict" data-testid="lift-dispatch-verdict">
          <div className="nx-device-drawer__row">
            <span>Dispatch</span>
            <StatusChip status={dispatch.chip} label={dispatch.label} />
          </div>
          <p className="nx-device-drawer__verdict-text">{dispatch.meaning}</p>
          <p className="nx-device-drawer__verdict-text nx-device-drawer__verdict-text--muted">
            {dispatch.action}
          </p>
        </div>

        <LiftRobotUseSection lift={lift} focus={focusRules} />

        <LiftWriteGatePanel />

        {/* The LIFT-WIDE standing refusal (ride verbs). Informational and
            deliberately a different element from the verb verdict below:
            this one says the lift is refusing robots' rides, that one
            says what happened to YOUR press. */}
        {standingRefusal && (
          <div
            className="nx-device-drawer__banner nx-device-drawer__refusal"
            data-testid="lift-refusal-banner"
            role="status"
          >
            {audience === 'ops' ? (
              <>
                <p>
                  <strong>Lift refusing rides: {standingRefusal.code}</strong>
                  {standingRefusal.permanent ? ' (permanent)' : ' (transient)'}
                </p>
                <p>{standingRefusal.reason}</p>
              </>
            ) : (
              // The code and the adapter's reason are ops' (BC-F9); the
              // hotel needs to know robots can't ride and for how long.
              <p>
                <strong>This lift is refusing robot rides</strong>
                {standingRefusal.permanent ? ' until it is fixed. Call Nexsoss.' : ' for now.'}
              </p>
            )}
            {sessionView && sessionView.retry_after_s > 0 && (
              <p className="nx-device-drawer__sub">
                {audience === 'ops'
                  ? `retry in ${Math.ceil(sessionView.retry_after_s)}s`
                  : `It tries again in ${Math.ceil(sessionView.retry_after_s)}s.`}
              </p>
            )}
          </div>
        )}

        {/* Ops only: it says so itself, "nothing to do here" (BC-F9). */}
        {legacyStanding && audience === 'ops' && (
          <p className="nx-device-drawer__sub" data-testid="lift-legacy-request">
            Legacy request pending drain: the supervisor still holds{' '}
            <code>{legacyStanding.session_id}</code>
            {legacyStanding.destination_floor ? ` → ${legacyStanding.destination_floor}` : ''}. The
            worker ends it on its own; nothing to do here.
          </p>
        )}

        <RoleGuard
          allowedRoles={['nexsoss:platform_admin', 'nexsoss:site_supervisor', 'nexsoss:service_operator']}
        >
          <form
            className="nx-device-drawer__form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!destination) return;
              openPress('manual_call');
              setPendingDestination(destination);
            }}
          >
            <label>
              Destination floor
              <select
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                disabled={noLiveState || leaseMissing || busy || operatorOwned || robotOwned}
                title={noLiveState ? noLiveStateTitle : undefined}
              >
                <option value="" disabled>
                  Select floor…
                </option>
                {lift.levels.map((lv) => (
                  <option key={lv} value={lv}>
                    {lv}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={
                noLiveState ||
                leaseMissing ||
                !destination ||
                busy ||
                dialogOpen ||
                operatorOwned ||
                robotOwned
              }
              data-testid="lift-request-btn"
              title={requestFloorTitle}
            >
              {call.isPending ? 'Requesting…' : 'Request Floor'}
            </button>

            {operatorOwned && (
              <>
                {/* The call being worked. Its verdict lands at ride
                    arrival, so the phase line is what the operator
                    watches in the meantime. */}
                <div className="nx-device-drawer__progress" data-testid="lift-call-progress">
                  {CALL_PROGRESS.map((step, i) => (
                    <span
                      key={step.phase}
                      className={
                        liftPhase && liftPhase.startsWith(step.phase)
                          ? 'nx-device-drawer__progress-step--current'
                          : undefined
                      }
                    >
                      {i > 0 ? '→ ' : ''}
                      {audience === 'ops' ? step.label : step.site}
                    </span>
                  ))}
                  {audience === 'ops' &&
                    liftPhase &&
                    !CALL_PROGRESS.some((s) => liftPhase.startsWith(s.phase)) && (
                      <span className="nx-device-drawer__progress-step--current">({liftPhase})</span>
                    )}
                </div>
                <button
                  type="button"
                  // Enabled while the call is in flight (gap 8): the
                  // adapter admits session.close from any phase, and this
                  // is the operator's only way to withdraw a call.
                  disabled={
                    close.isPending || (op.state.kind === 'inflight' && op.state.verb === 'manual_close')
                  }
                  data-testid="lift-cancel-call-btn"
                  title="End your call and hand the car back to public service"
                  onClick={() => {
                    if (!owner) return;
                    runVerb('manual_close', close, {
                      idempotency_key: operatorIdempotencyKey(
                        lift,
                        'close',
                        owner.id,
                        pressNonce('manual_close'),
                      ),
                    });
                  }}
                >
                  {close.isPending ? 'Cancelling…' : 'Cancel call'}
                  {autoCloseIn !== null ? ` (auto-closes in ${autoCloseIn}s)` : ''}
                </button>
              </>
            )}

            {robotOwned && wait?.kind === 'agv_mode' && (
              // NOT a button. False for agv_mode ALWAYS: the car grants
              // AGV control or it does not, and an operator's confirmation
              // is not evidence of it. Pressing Release here is what the
              // edge refused NOT_ADVANCEABLE on 2026-08-29.
              <p className="nx-device-drawer__sub" data-testid="lift-agv-wait">
                {audience === 'site' ? (
                  SITE_LIFT_WORDS.agvWait(waitRemaining)
                ) : (
                  <>
                    Waiting for the car to grant AGV control
                    {sessionView?.mode_label ? ` (${sessionView.mode_label})` : ''}; this wait
                    cannot be confirmed by an operator
                    {waitRemaining !== null ? ` — ${waitRemaining}s` : ''}
                  </>
                )}
              </p>
            )}

            {robotOwned && wait?.kind !== 'agv_mode' && (
              <button
                type="button"
                // `advance_eligible` is the ONLY thing this keys on. Not
                // "is there a phase" -- there was, and it was the AGV wait.
                disabled={busy || dialogOpen || !advanceEligible}
                data-testid="lift-advance-btn"
                title={
                  audience === 'site'
                    ? advanceEligible && wait
                      ? siteConfirmMessage(lift.name, wait.name)
                      : wait
                        ? SITE_LIFT_WORDS.notConfirmable
                        : SITE_LIFT_WORDS.nothingWaiting
                    : advanceEligible && wait
                      ? `Confirm "${wait.name}" (phase: ${liftPhase ?? 'unknown'}). ` +
                        'Only do this if you can see the car. It does not command the lift — ' +
                        'it supplies an observation the bridge could not make itself.'
                      : wait
                        ? `The ride is waiting on "${wait.name}", which an operator cannot confirm`
                        : 'The ride is not waiting on anything an operator can confirm'
                }
                onClick={() => {
                  openPress('advance');
                  setPendingAdvance(true);
                }}
              >
                {advance.isPending
                  ? 'Releasing…'
                  : advanceEligible && wait
                    ? audience === 'site'
                      ? siteConfirmLabel(wait.name, waitRemaining)
                      : `Confirm ${wait.name}${waitRemaining !== null ? ` (${waitRemaining}s)` : ''}`
                    : 'Release lift'}
              </button>
            )}

            {robotOwned && (
              <>
                <button
                  type="button"
                  disabled={busy || dialogOpen}
                  data-testid="lift-recover-btn"
                  title={
                    audience === 'site'
                      ? SITE_LIFT_WORDS.recoverTitle
                      : "Ends the lift session and cancels the robot's task. It does not move the car or the robot first. When the robot is clear of the cabin, the fleet adapter is also asked to drop RMF's claim, so the car goes back to public service. If the robot is in the cabin, that release is refused, RMF keeps its claim and will call the car again: get the robot out first, then press again."
                  }
                  onClick={() => {
                    openPress('recover');
                    setPendingRecover(true);
                  }}
                >
                  Recover…
                </button>
                <button
                  type="button"
                  disabled={busy || dialogOpen}
                  data-testid="lift-end-session-btn"
                  title={
                    audience === 'site'
                      ? SITE_LIFT_WORDS.endSessionTitle
                      : "Force-ends the RMF session holding this lift (the owner shown above). It does not move the robot first. When the robot is clear of the cabin, the fleet adapter is also asked to drop RMF's claim, so the car goes back to public service. If the robot is in the cabin, that release is refused, RMF keeps its claim and will call the car again: get the robot out first, then press again. Unlike Recover, the robot's task is not cancelled."
                  }
                  onClick={() => {
                    openPress('end_rmf_session');
                    setPendingEndSession(true);
                  }}
                >
                  {/* The owner's wire id (`phantas_fleet/NomNom`) is ops'
                      only; the hotel reads the verb. */}
                  {audience === 'ops' ? `End lift session (${owner?.id})…` : 'End lift session…'}
                </button>
              </>
            )}

            {(sessionError || sessionExpired) && (
              <span className="nx-device-drawer__error" role="alert">
                {sessionError ?? 'Session expired — close and reopen this drawer to request another.'}
              </span>
            )}

            {/* The in-flight line, then the verdict banner (S3). One
                banner for all five verbs; the CommandRecord's "Accepted"
                line is gone, because admission is not the outcome. */}
            {op.state.kind === 'inflight' && (
              <div
                className="nx-device-drawer__banner nx-device-drawer__banner--inflight"
                data-testid="lift-verb-inflight"
                role="status"
              >
                <p>
                  <span aria-hidden="true">⏳ </span>
                  {VERB_LABEL[op.state.verb] ?? op.state.verb} sent —{' '}
                  {audience === 'ops' ? 'awaiting the verdict' : 'waiting for the answer'} (
                  {Math.max(0, Math.ceil(op.state.deadline_s - (now - op.state.sentAt) / 1000))}s)
                </p>
                {/* The request id is a log key: ops only (BC-F9). */}
                {audience === 'ops' && (
                  <p className="nx-device-drawer__sub">request {op.state.request_id}</p>
                )}
              </div>
            )}
            {op.state.kind === 'verdict' && (
              <div
                className={`nx-device-drawer__banner nx-device-drawer__banner--${op.state.status}`}
                data-testid="lift-verb-verdict"
                data-status={op.state.status}
                data-code={op.state.code ?? ''}
                role="status"
              >
                <div className="nx-device-drawer__banner-head">
                  <strong>
                    {VERB_LABEL[op.state.verb] ?? op.state.verb}: {STATUS_LABEL[op.state.status] ?? op.state.status}
                    {audience === 'ops' && op.state.code ? ` (${op.state.code})` : ''}
                  </strong>
                  <button
                    type="button"
                    className="nx-device-drawer__banner-dismiss"
                    aria-label="Dismiss verdict"
                    data-testid="lift-verb-verdict-dismiss"
                    onClick={op.dismiss}
                  >
                    ×
                  </button>
                </div>
                {/* The codes, the wire reason and the wait and phase
                    names are ops'; a site reader gets a sentence for the
                    code, or for the status when the code has none (BC-F9). */}
                {audience === 'ops' ? (
                  <p>{(op.state.code && VERDICT_CODE_TEXT[op.state.code]) ?? op.state.reason}</p>
                ) : (
                  <p>{siteVerdictText(op.state)}</p>
                )}
                {audience === 'ops' && op.state.code && VERDICT_CODE_TEXT[op.state.code] && op.state.reason && (
                  <p className="nx-device-drawer__sub">{op.state.reason}</p>
                )}
                {audience === 'ops' && op.state.status === 'refused' && (op.state.wait_name || op.state.phase) && (
                  <p className="nx-device-drawer__sub">
                    {op.state.wait_name ? `waiting on: ${op.state.wait_name}` : ''}
                    {op.state.wait_name && op.state.phase ? ' · ' : ''}
                    {op.state.phase ? `phase: ${op.state.phase}` : ''}
                  </p>
                )}
              </div>
            )}
          </form>
        </RoleGuard>
      </div>

      <ConfirmDialog
        open={pendingAdvance}
        title="Confirm this step?"
        message={
          audience === 'site'
            ? siteConfirmMessage(lift.name, wait?.name)
            : `${lift.name} is waiting on "${wait?.name ?? liftPhase ?? 'unknown'}" (phase ` +
              `${liftPhase ?? 'unknown'}). Confirming tells the bridge that the step it cannot ` +
              'observe has happened — that the car arrived, the doors are open, or the robot ' +
              'is clear. Only do this if you can SEE the car. It does not move the lift, and ' +
              'it cannot confirm that AGV control was granted.'
        }
        confirmLabel="Confirm step"
        cancelLabel="Cancel"
        variant="warning"
        onConfirm={() => {
          markActivity();
          setPendingAdvance(false);
          // Re-checked here, not just at click: the dialog can sit open
          // across a phase change, and the wait it was opened for may be
          // gone.
          if (!liftPhase || !advanceEligible || !wait) return;
          runVerb('advance', advance, {
            expected_phase: liftPhase,
            idempotency_key: operatorIdempotencyKey(
              lift,
              'advance',
              `${liftPhase}:${wait.started_epoch_ts ?? 0}`,
              pressNonce('advance'),
            ),
          });
        }}
        onCancel={() => {
          markActivity();
          setPendingAdvance(false);
        }}
      />

      <ConfirmDialog
        open={pendingRecover}
        title="Recover the stuck robot?"
        message={
          audience === 'site'
            ? `End the robot's hold on ${lift.name} and cancel its job. ${SITE_LIFT_WORDS.stillInCar}`
            : `End the lift session holding ${lift.name}` +
              `${owner ? ` (${owner.id})` : ''} and cancel the robot's task. Nothing moves the ` +
              'car or the robot first: if the robot is in the car or the doorway, it stays ' +
              'there. When the robot is clear of the cabin, the fleet adapter is also asked to ' +
              "drop RMF's claim on the car, so the car goes back to public service and RMF does " +
              'not call it straight back. If the robot is in the cabin, the adapter refuses ' +
              'that release, RMF keeps its claim and will call the car again: get the robot ' +
              'out first, then press again.'
        }
        confirmLabel="Recover"
        cancelLabel="Cancel"
        variant="warning"
        onConfirm={() => {
          markActivity();
          setPendingRecover(false);
          if (!owner || owner.kind !== 'robot') return;
          runVerb('recover', recover, {
            idempotency_key: operatorIdempotencyKey(
              lift,
              'recover',
              owner.id,
              pressNonce('recover'),
            ),
          });
        }}
        onCancel={() => {
          markActivity();
          setPendingRecover(false);
        }}
      />

      <ConfirmDialog
        open={pendingEndSession}
        title="End the lift session?"
        message={
          audience === 'site'
            ? `End the robot's hold on ${lift.name}. Its job is not cancelled, so it may call the lift again. ${SITE_LIFT_WORDS.stillInCar}`
            : `Force-end the session holding ${lift.name}` +
              `${owner ? ` (${owner.id})` : ''}. This does NOT move or wait for a robot. ` +
              `When the robot is clear of the cabin, the fleet adapter is also asked to drop ` +
              `RMF's claim on the car, so the car goes back to public service and RMF does not ` +
              `call it straight back. If the robot is in the cabin, the adapter refuses that ` +
              `release, RMF keeps its claim and will call the car again: get the robot out ` +
              `first, then press again. Unlike "Recover", the robot's task is not cancelled.`
        }
        confirmLabel="End session"
        cancelLabel="Cancel"
        variant="warning"
        onConfirm={() => {
          markActivity();
          setPendingEndSession(false);
          // Keyed on the ADAPTER's owner, not `rmfSessionId`: the
          // supervisor's echo lags and is empty for a car the adapter
          // already believes a robot holds. `owner.id` for a robot IS
          // the RMF session id (`<fleet>/<robot>`), which is what the
          // override field needs.
          if (!owner || owner.kind !== 'robot') return;
          runVerb('end_rmf_session', endSession, {
            override_rmf_session_id: owner.id,
            idempotency_key: operatorIdempotencyKey(
              lift,
              'end',
              owner.id,
              pressNonce('end_rmf_session'),
            ),
          });
        }}
        onCancel={() => {
          markActivity();
          setPendingEndSession(false);
        }}
      />

      <ConfirmDialog
        open={pendingDestination !== null}
        title="Request lift floor?"
        message={pendingDestination ? `Call ${lift.name} to ${pendingDestination}?` : ''}
        confirmLabel="Request"
        cancelLabel="Cancel"
        variant="warning"
        onConfirm={() => {
          markActivity();
          // `session` itself never gets cleared on expiry -- only the
          // derived `sessionExpired` boolean flips -- so checking
          // `session` alone here (as this used to) let a request
          // opened BEFORE expiry (the confirm dialog can sit open for
          // as long as the user takes to click "Request") still fire
          // AFTER the session had expired, bypassing the same guard
          // the form's own buttons already enforce.
          //
          // `hasLiveState` is re-read for exactly the same reason: a
          // bridge that stops publishing while this dialog is open
          // turns the car into a registry-only row under a drawer that
          // never remounts (it is keyed on the lift's name), and the
          // call would go to an adapter that will never answer it.
          if (pendingDestination && !noLiveState && session && !sessionExpired) {
            runVerb('manual_call', call, {
              session_id: session.session_id,
              floor: pendingDestination,
              idempotency_key: operatorIdempotencyKey(
                lift,
                'call',
                `${pendingDestination}:${session.session_id}`,
                pressNonce('manual_call'),
              ),
            });
          }
          setPendingDestination(null);
        }}
        onCancel={() => {
          markActivity();
          setPendingDestination(null);
        }}
      />
    </aside>
  );
}
